/**
 * Format date for autopay display
 * Returns format: "MMM d, yyyy" (e.g., "Jan 5, 2024")
 * Returns fallback if date is empty or invalid
 * 
 * @param dateStr - ISO date string to format
 * @param fallback - Fallback string if date is empty (default: 'End of lease')
 * @returns Formatted date string or fallback
 */
export const formatAutopayDate = (dateStr?: string | null, fallback: string = 'End of lease'): string => {
  if (!dateStr) return fallback;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch {
    return '—';
  }
};

/**
 * Format autopay amount for display
 * Returns "Full balance" if isFullAmount or no amount provided
 * Otherwise returns formatted currency string
 * 
 * @param amount - The amount value
 * @param isFullAmount - Whether this is a full balance amount
 * @returns Formatted amount string
 */
export const formatAutopayAmount = (amount?: number, isFullAmount?: boolean): string => {
  if (isFullAmount || !amount) return 'Full balance';
  return `$${amount.toFixed(2)}`;
};

/**
 * Get next month's first day formatted for autopay start date display
 * Returns format: "MMM d, yyyy" (e.g., "Jan 1, 2024")
 */
export const getNextMonthFirstDay = (): string => {
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  return nextMonth.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatConnectedSince = (isoDate?: string | null): string | null => {
  if (!isoDate) return null;
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    const formatted = date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    return `Connected Since ${formatted}`;
  } catch {
    return null;
  }
};

/**
 * Format invitation date for display
 * Returns format: "MMM d, yyyy" (e.g., "Jan 15, 2024")
 * 
 * @param isoDate - ISO date string to format
 * @returns Formatted date string or the original string if parsing fails
 */
export const formatInvitationDate = (isoDate?: string | null): string => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return isoDate;
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  } catch {
    return isoDate;
  }
};

/**
 * Check if 24 hours have passed since a given date
 * Used to determine if resend action is available for invitations
 * 
 * @param isoDate - ISO date string to check
 * @returns true if 24+ hours have passed, false otherwise
 */
export const hasPassedHours = (isoDate?: string | null, hours: number = 24): boolean => {
  if (!isoDate) return false;
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return false;
    const now = new Date();
    const hoursDiff = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    return hoursDiff >= hours;
  } catch {
    return false;
  }
};

/**
 * Format date for notification display
 * Returns format: "MMM d" (e.g., "Aug 27")
 * 
 * @param dateStr - ISO date string to format
 * @returns Formatted date string
 */
export const formatDateMMMdd = (dateStr: string): string => {
  const date = new Date(dateStr);
  const month = date.toLocaleString('default', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day}`;
};

/**
 * Format date for notification display with year
 * Returns format: "MMM d, yyyy" (e.g., "Aug 27, 2024")
 * 
 * @param dateStr - ISO date string to format
 * @returns Formatted date string with year
 */
export const formatDateMMMddyyyy = (dateStr: string): string => {
  const date = new Date(dateStr);
  const month = date.toLocaleString('default', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};
